/**
 * /p/contrato/:token — pagina publica para cliente assinar contrato.
 *
 * Standalone (sem chrome do ERP). Renderiza HTML do contrato + form
 * de assinatura (nome + email + CPF/CNPJ opcional + checkbox "Aceito
 * os termos"). Apos assinatura, exibe confirmacao com numero do pedido
 * criado.
 *
 * Validade juridica: Lei 14.063/2020. Backend captura IP + User-Agent +
 * timestamp automaticamente. Audit log persiste tudo.
 */
import React, { useEffect, useState } from "react";
import {
  FiAlertCircle,
  FiCheck,
  FiClock,
  FiDownload,
  FiInfo,
  FiX,
} from "react-icons/fi";
import { useParams } from "react-router-dom";

import HtmlTemplateFrame from "../../components/HtmlTemplateFrame";
import { ACCESS_API_URL } from "../../config/api";
import {
  assinarContratoPublico,
  recusarContratoPublico,
  visualizarContratoPublico,
} from "../../services/contratos";

import {
  ActionBar,
  ApproveButton,
  Container,
  Header,
  HeaderInner,
  Modal,
  ModalActions,
  ModalContent,
  RejectButton,
  SecondaryButton,
  StateBox,
  Subtitle,
  Textarea,
  Title,
} from "../OrcamentoPublico/styles";

export default function ContratoPublico() {
  const { token } = useParams();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [confirmando, setConfirmando] = useState(null); // null | "assinar" | "recusar"
  const [salvando, setSalvando] = useState(false);
  const [sucessoMensagem, setSucessoMensagem] = useState(null);

  // Form de assinatura
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [documento, setDocumento] = useState("");
  const [aceiteTermos, setAceiteTermos] = useState(false);

  // Form de recusa
  const [motivo, setMotivo] = useState("");

  // SS3.A.2: a sanitizacao (DOMPurify) e o isolamento agora moram no
  // HtmlTemplateFrame — iframe sem allow-scripts, <style> preservado.

  useEffect(() => {
    if (!token) {
      setErro("Token inválido.");
      setLoading(false);
      return;
    }
    visualizarContratoPublico(token)
      .then((d) => {
        setDados(d);
        setErro(null);
      })
      .catch((e) => {
        setErro(e?.message || "Erro ao carregar contrato.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAssinar = async () => {
    if (!nome.trim() || !email.trim() || !aceiteTermos) return;
    setSalvando(true);
    setErro(null);
    try {
      const res = await assinarContratoPublico(token, {
        nome: nome.trim(),
        email: email.trim(),
        documento: documento.trim(),
        aceite_termos: true,
      });
      setSucessoMensagem(res?.detalhe || "Contrato assinado com sucesso.");
      setConfirmando(null);
      // Recarrega para mostrar estado terminal
      const d = await visualizarContratoPublico(token);
      setDados(d);
    } catch (e) {
      setErro(e?.message || "Erro ao assinar.");
    } finally {
      setSalvando(false);
    }
  };

  const handleRecusar = async () => {
    if (!motivo.trim()) return;
    setSalvando(true);
    setErro(null);
    try {
      await recusarContratoPublico(token, motivo.trim());
      setSucessoMensagem("Recusa registrada. Obrigado pelo retorno.");
      setConfirmando(null);
      const d = await visualizarContratoPublico(token);
      setDados(d);
    } catch (e) {
      setErro(e?.message || "Erro ao recusar.");
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <HeaderInner>
            <Title>Carregando contrato...</Title>
          </HeaderInner>
        </Header>
      </Container>
    );
  }

  if (erro && !dados) {
    return (
      <Container>
        <Header>
          <HeaderInner>
            <Title>Erro</Title>
          </HeaderInner>
        </Header>
        <CardWrapper>
          <StateBox $tone="danger">
            <FiX size={32} aria-hidden="true" />
            <h2>Não foi possível carregar o contrato</h2>
            <p>{erro}</p>
          </StateBox>
        </CardWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderInner>
          <Title>Contrato {dados.numero}</Title>
          <Subtitle>{dados.cliente_nome}</Subtitle>
        </HeaderInner>
      </Header>

      <CardWrapper>
        {/* HTML renderizado pelo backend (Jinja2 sandbox) num iframe
            isolado (HtmlTemplateFrame): preserva o bloco <style> do
            template — que o DOMPurify em div removia, deixando o contrato
            "todo branco" — sem vazar o CSS para esta pagina. A defesa em
            profundidade (SS3.A.2) continua: DOMPurify remove script tags e
            event handlers; sandbox sem allow-scripts e a barreira final.
            FULL-BLEED: o template ocupa a viewport inteira como pagina web
            (o card de 900px que existia aqui espremia o design numa
            "folha" no meio da tela); os botoes seguem no ActionWrapper. */}
        <div style={{ background: "#fff" }}>
          <HtmlTemplateFrame
            fullBleed
            html={dados.html_renderizado}
            titulo={`Contrato ${dados.numero || ""}`}
          />
        </div>

        {/* Copia em PDF do documento exato (html_renderizado persistido). */}
        <ActionWrapper>
          <ActionBar>
            <SecondaryButton
              as="a"
              href={`${ACCESS_API_URL}/publico/contrato/${token}/pdf`}
              download
              style={{ textDecoration: "none", textAlign: "center" }}
            >
              <FiDownload size={16} aria-hidden="true" />
              {" "}Baixar PDF
            </SecondaryButton>
          </ActionBar>
        </ActionWrapper>

        {/* Acoes (assinar/recusar) ou estado terminal */}
        <ActionWrapper>
          {dados.pode_assinar ? (
            <ActionBar>
              <ApproveButton
                type="button"
                onClick={() => setConfirmando("assinar")}
                disabled={salvando}
              >
                <FiCheck size={18} aria-hidden="true" />
                Assinar contrato
              </ApproveButton>
              <RejectButton
                type="button"
                onClick={() => setConfirmando("recusar")}
                disabled={salvando}
              >
                <FiX size={18} aria-hidden="true" />
                Recusar
              </RejectButton>
            </ActionBar>
          ) : (
            renderEstadoTerminal(dados)
          )}

          {sucessoMensagem && (
            <StateBox $tone="success" style={{ marginTop: 16 }}>
              <FiCheck size={28} aria-hidden="true" />
              <p>{sucessoMensagem}</p>
            </StateBox>
          )}
        </ActionWrapper>
      </CardWrapper>

      {/* Modal de assinatura */}
      {confirmando === "assinar" && (
        <Modal onClick={() => !salvando && setConfirmando(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Assinar contrato {dados.numero}</h3>
            <p>
              Para assinar eletronicamente, preencha seus dados abaixo e
              marque o aceite. Esta assinatura tem validade jurídica
              conforme Lei 14.063/2020.
            </p>

            <Label>Nome completo *</Label>
            <Input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Como aparece no documento de identidade"
              maxLength={200}
              autoFocus
              required
            />

            <Label>Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              maxLength={254}
              required
            />

            <Label>CPF ou CNPJ (opcional)</Label>
            <Input
              type="text"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              maxLength={20}
            />
            <small style={{ color: "#666", fontSize: "0.78rem" }}>
              Opcional, mas recomendado — refor&ccedil;a a validade jurídica
              da assinatura.
            </small>

            <CheckboxLabel>
              <input
                type="checkbox"
                checked={aceiteTermos}
                onChange={(e) => setAceiteTermos(e.target.checked)}
              />
              <span>
                <strong>Li e aceito os termos</strong> deste contrato.
                Estou ciente que esta ação tem validade jurídica conforme
                Lei 14.063/2020 e que meus dados (nome, email, IP,
                data/hora) serão registrados como prova de autoria.
              </span>
            </CheckboxLabel>

            {erro && (
              <ErroInline>
                <FiAlertCircle size={14} /> {erro}
              </ErroInline>
            )}

            <ModalActions>
              <SecondaryButton
                type="button"
                onClick={() => setConfirmando(null)}
                disabled={salvando}
              >
                Cancelar
              </SecondaryButton>
              <ApproveButton
                type="button"
                onClick={handleAssinar}
                disabled={
                  salvando || !nome.trim() || !email.trim() || !aceiteTermos
                }
                style={{ flex: "0 0 auto", padding: "10px 18px", fontSize: "0.95rem", minHeight: "auto" }}
              >
                {salvando ? "Assinando..." : "Confirmar assinatura"}
              </ApproveButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}

      {/* Modal de recusa */}
      {confirmando === "recusar" && (
        <Modal onClick={() => !salvando && setConfirmando(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Recusar contrato</h3>
            <p>
              Informe o motivo da recusa. O vendedor será notificado e
              poder&aacute; gerar um novo contrato com os ajustes necess&aacute;rios.
            </p>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={5}
              maxLength={1000}
              placeholder="Ex: gostaria de alterar prazo, valor, condicoes de pagamento..."
              autoFocus
              required
            />
            {erro && (
              <ErroInline>
                <FiAlertCircle size={14} /> {erro}
              </ErroInline>
            )}
            <ModalActions>
              <SecondaryButton
                type="button"
                onClick={() => setConfirmando(null)}
                disabled={salvando}
              >
                Cancelar
              </SecondaryButton>
              <RejectButton
                type="button"
                onClick={handleRecusar}
                disabled={salvando || !motivo.trim()}
                style={{ flex: "0 0 auto", padding: "10px 18px", fontSize: "0.95rem", minHeight: "auto" }}
              >
                {salvando ? "Enviando..." : "Recusar"}
              </RejectButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

function renderEstadoTerminal(dados) {
  const data = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "");
  switch (dados.situacao) {
    case "ASSINADO":
      return (
        <StateBox $tone="success">
          <FiCheck size={32} aria-hidden="true" />
          <h2>Contrato assinado</h2>
          <p>
            Assinado por <strong>{dados.assinante_nome}</strong> em {data(dados.assinado_em)}.
            Em breve você será contatado para os próximos passos.
          </p>
        </StateBox>
      );
    case "RECUSADO":
      return (
        <StateBox $tone="neutral">
          <FiX size={32} aria-hidden="true" />
          <h2>Contrato recusado</h2>
          <p>
            Recusado em {data(dados.recusado_em)}.
            {dados.motivo_recusa ? ` Motivo: ${dados.motivo_recusa}` : ""}
            {" "}O vendedor pode gerar um novo contrato com ajustes.
          </p>
        </StateBox>
      );
    case "EXPIRADO":
      return (
        <StateBox $tone="warning">
          <FiClock size={32} aria-hidden="true" />
          <h2>Link expirado</h2>
          <p>Solicite ao vendedor um novo contrato.</p>
        </StateBox>
      );
    default:
      return (
        <StateBox $tone="info">
          <FiInfo size={32} aria-hidden="true" />
          <h2>Ação indisponível</h2>
          <p>Este contrato não pode ser respondido neste momento.</p>
        </StateBox>
      );
  }
}

// Componentes locais inline (re-uso de OrcamentoPublico styles + customs)
function CardWrapper({ children }) {
  return <div>{children}</div>;
}
function ActionWrapper({ children }) {
  return (
    <div style={{
      maxWidth: 760, margin: "16px auto 0", padding: "0 16px",
    }}>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: "0.78rem",
      color: "#666",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      marginTop: 12,
      marginBottom: 4,
      fontWeight: 600,
    }}>
      {children}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        padding: "10px 12px",
        border: "1px solid #ddd",
        borderRadius: 6,
        fontSize: "0.95rem",
        boxSizing: "border-box",
        ...(props.style || {}),
      }}
    />
  );
}

function CheckboxLabel({ children }) {
  return (
    <label style={{
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      marginTop: 16,
      padding: 12,
      background: "#f9fafb",
      border: "1px solid #e5e7eb",
      borderRadius: 6,
      fontSize: "0.88rem",
      color: "#374151",
      cursor: "pointer",
    }}>
      {children}
    </label>
  );
}

function ErroInline({ children }) {
  return (
    <div style={{
      marginTop: 12,
      padding: 10,
      background: "#fee2e2",
      color: "#991b1b",
      borderRadius: 6,
      fontSize: "0.88rem",
      display: "flex",
      alignItems: "center",
      gap: 6,
    }}>
      {children}
    </div>
  );
}
